// apiMiddleware.ts
import {
  SharedUserConfig,
  SharedUserConfigurationAPI,
} from 'app/interfaces/SharedUserConfiguration';
import { StoreWithViewerConfiguration } from 'app/redux/viewerConfiguration/reducer';
import { StoreWithProcessingState } from 'app/redux/processing/reducer';
import { StoreWithXRaygeneratorconfiguration } from 'app/redux/xrayGeneratorConfiguration/reducer';
import _ from 'lodash';
import type { Action, Dispatch, Middleware, MiddlewareAPI } from 'redux';

type SharedUserConfigurationPath = keyof SharedUserConfig;

interface UserConfigurationAction extends Action {
  syncWithUserConfigurationAPI?: SharedUserConfigurationPath | boolean; // Use a string to specify a path to specific configuration to save
  meta?: {
    syncWithUserConfigurationAPI?: SharedUserConfigurationPath | boolean; // Use a string to specify a path to specific configuration to save
  }; // This is done to be aligned with flux standard action enforced by redux/toolkit
}

export type StoreWithSharedUserConfiguration = StoreWithViewerConfiguration &
  StoreWithXRaygeneratorconfiguration &
  StoreWithProcessingState;

function convertStoreToUserConfiguration<S extends StoreWithSharedUserConfiguration>(
  store: MiddlewareAPI<S>,
  syncWithUserConfigurationAPIPath: SharedUserConfigurationPath | boolean = undefined // Use a string to specify a path to specific configuration to save
): SharedUserConfig {
  const state = store.getState();
  const fullConfig = {
    noXRayDeletion: state.viewerConfiguration?.noXRayDeletion,
    acquisitionConstants: state.xRayConfiguration?.acquisitionConstants,
    processingFilters: state.processing?.filters,
    isProcessingFineTuningEnabled: state.viewerConfiguration?.isProcessingFineTuningEnabled,
  };
  if (typeof syncWithUserConfigurationAPIPath === 'string') {
    return _.pick(fullConfig, syncWithUserConfigurationAPIPath) as SharedUserConfig;
  }
  return fullConfig;
}

export default function createUserConfigurationAPIMiddleware(
  api: SharedUserConfigurationAPI
): Middleware {
  return <S>(store: MiddlewareAPI<S>) =>
    (next: Dispatch<S>) =>
    <A extends Action>(action: A) => {
      // Let the action flow through first
      const result = next(action);
      const userConfigAction = action as UserConfigurationAction;
      // Check if this action should trigger API sync
      if (
        userConfigAction.syncWithUserConfigurationAPI ||
        userConfigAction.meta?.syncWithUserConfigurationAPI
      ) {
        // Send updated state to API
        const sharedUserConfig = convertStoreToUserConfiguration(
          store as MiddlewareAPI<StoreWithSharedUserConfiguration>,
          userConfigAction.syncWithUserConfigurationAPI ??
            userConfigAction.meta?.syncWithUserConfigurationAPI
        );
        // Retry mechanism should be implemented here
        api?.update(sharedUserConfig).catch((error) => {
          console.warn('Failed to sync with API:', error);
        });
      }

      return result;
    };
}
